import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Banknote, DoorOpen, Loader2, Minus, Plus, Smartphone, WifiOff } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Drawer, DrawerContent, DrawerDescription, DrawerTitle } from "@/components/ui/drawer";
import { ConfirmDialog } from "@/components/admin/Fields";
import { errorMessage, orgCancel, orgDoorSale } from "@/lib/api";
import { fmtKr } from "@/lib/tickets";
import { cn } from "@/lib/utils";

const newKey = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);
const isNetworkError = (e) => !e?.response;
const queueKey = (eventId) => `hk-door-queue-${eventId}`;
const readQueue = (eventId) => { try { return JSON.parse(window.localStorage.getItem(queueKey(eventId)) || "[]"); } catch { return []; } };
const writeQueue = (eventId, q) => window.localStorage.setItem(queueKey(eventId), JSON.stringify(q));

/** Sales made while the phone had no signal wait in localStorage and are replayed (same Idempotency-Key → never twice). */
const useOfflineQueue = (token, eventId, onFlushed) => {
  const [pending, setPending] = useState(() => readQueue(eventId).length);
  const busy = useRef(false);
  const flush = useCallback(async () => {
    if (busy.current) return;
    busy.current = true;
    let q = readQueue(eventId);
    for (const item of [...q]) {
      try {
        await orgDoorSale(token, eventId, item.body, item.key);
        q = q.filter((x) => x.key !== item.key);
      } catch (e) {
        if (isNetworkError(e)) break; // still offline – try again later
        q = q.filter((x) => x.key !== item.key); // the server said no (full/invalid) – drop it and tell the organiser
        toast.error(`Dørsal (${item.body.qty}) vart ikkje registrert: ${errorMessage(e)}`);
      }
    }
    writeQueue(eventId, q);
    setPending(q.length);
    busy.current = false;
    if (q.length === 0) onFlushed?.();
  }, [token, eventId, onFlushed]);
  useEffect(() => {
    flush();
    const id = window.setInterval(flush, 15000);
    window.addEventListener("online", flush);
    return () => { window.clearInterval(id); window.removeEventListener("online", flush); };
  }, [flush]);
  const enqueue = useCallback((key, body) => { const q = [...readQueue(eventId), { key, body, at: Date.now() }]; writeQueue(eventId, q); setPending(q.length); }, [eventId]);
  return { pending, enqueue, flush };
};

const Big = ({ children, className, ...p }) => <Button type="button" size="lg" className={cn("h-16 flex-1 rounded-[14px] text-[17px] active:scale-[0.98]", className)} {...p}>{children}</Button>;

/** «+ Selt i døra»: the button and its half-height sheet. One tap on Kontant/Vipps *is* the save. */
export const DoorSale = ({ token, eventId, title, door, onSold, onChanged, className }) => {
  const [open, setOpen] = useState(false);
  const [qty, setQty] = useState(1);
  const [typeId, setTypeId] = useState(null);
  const [overAsk, setOverAsk] = useState(null);
  const types = door?.types;
  const type = useMemo(() => (types || []).find((t) => t.id === typeId) || (types || [])[0], [types, typeId]);
  const left = door?.door_available ?? null;
  const full = left !== null && left <= 0;
  const noHold = full && !door?.released && !(door?.sold_door > 0);
  const overQuota = left !== null && qty > left;
  const amount = (type?.price || 0) * qty;
  const { pending: queued, enqueue } = useOfflineQueue(token, eventId, onChanged);
  const lock = useRef(false); // two taps in the same tick must not become two sales

  const sell = useMutation({
    mutationFn: ({ body, key }) => orgDoorSale(token, eventId, body, key),
    onSettled: () => { lock.current = false; },
    onSuccess: (d, { body }) => {
      setOpen(false); setQty(1); setOverAsk(null);
      onSold?.(d.order);
      onChanged?.();
      const label = `${body.qty} ${body.qty === 1 ? "billett" : "billettar"} selt i døra · ${body.payment === "cash" ? "kontant" : "Vipps"}${d.order.amount ? ` · ${fmtKr(d.order.amount)}` : ""}`;
      toast.success(d.duplicate ? "Alt registrert (same sal)" : label, { duration: 8000, action: d.duplicate ? undefined : { label: "Angre", onClick: () => orgCancel(token, d.order.id).then(() => { onChanged?.(); toast.success("Dørsalet er angra"); }).catch((e) => toast.error(errorMessage(e))) } });
    },
    onError: (e, { body, key }) => {
      if (isNetworkError(e)) { enqueue(key, body); setOpen(false); setQty(1); toast.message("Ikkje nett – salet er lagra på telefonen og vert sendt automatisk", { icon: <WifiOff className="h-4 w-4" /> }); onSold?.(null); return; }
      if (e?.response?.status === 409 && !body.allow_over) { setOverAsk({ body, key }); return; }
      toast.error(errorMessage(e));
    },
  });

  const fire = (body, key) => { if (lock.current) return; lock.current = true; sell.mutate({ body, key }); };
  const start = (payment) => {
    if (lock.current || sell.isPending) return;
    const body = { qty, type_id: type?.id || null, payment, allow_over: false };
    const key = newKey();
    if (overQuota) { setOverAsk({ body, key }); return; }
    fire(body, key);
  };

  return (
    <div className={className}>
      <Button type="button" size="lg" onClick={() => setOpen(true)} className={cn("h-14 w-full rounded-[14px] text-[16px] sm:w-auto sm:px-6", full && "bg-secondary text-foreground hover:bg-secondary/80")} data-testid="door-sale-open">
        <DoorOpen className="h-5 w-5" /> {full ? (noHold ? "Ingen dørkvote – sel likevel" : "Fullt i døra – sel likevel") : "+ Selt i døra"}
        {queued > 0 && <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-sol px-2 py-0.5 text-[11px] font-semibold text-ink" data-testid="door-sale-queued"><WifiOff className="h-3 w-3" />{queued}</span>}
      </Button>

      <Drawer open={open} onOpenChange={setOpen} shouldScaleBackground={false}>
        <DrawerContent className="rounded-t-[24px] border-border bg-background" data-testid="door-sale-sheet">
          <div className="mx-auto w-full max-w-md px-5 pb-6 pt-2 safe-bottom">
            <DrawerTitle className="t-h3">Selt i døra</DrawerTitle>
            <DrawerDescription className="mt-1 text-[14px] text-muted-foreground">{title}{left !== null ? ` · ${left} ${left === 1 ? "plass" : "plassar"} att i døra` : " · utan kapasitetsgrense"}</DrawerDescription>

            <div className="mt-6 flex items-center justify-between gap-4" role="group" aria-label="Tal billettar" data-testid="door-sale-qty">
              <Button type="button" variant="outline" size="icon" className="h-14 w-14 rounded-full bg-card" onClick={() => setQty((q) => Math.max(1, q - 1))} disabled={qty <= 1} aria-label="Færre" data-testid="door-sale-qty-dec"><Minus className="h-5 w-5" /></Button>
              <div className="text-center"><div className="font-display text-[48px] font-semibold leading-none tabular" aria-live="polite" data-testid="door-sale-qty-value">{qty}</div><div className="t-mono-sm mt-1 text-muted-foreground">{qty === 1 ? "billett" : "billettar"}</div></div>
              <Button type="button" variant="outline" size="icon" className="h-14 w-14 rounded-full bg-card" onClick={() => setQty((q) => Math.min(50, q + 1))} aria-label="Fleire" data-testid="door-sale-qty-inc"><Plus className="h-5 w-5" /></Button>
            </div>
            <div className="mt-3 flex justify-center gap-2">{[1, 2, 3, 4, 5].map((n) => <button key={n} type="button" onClick={() => setQty(n)} className={cn("h-10 w-10 rounded-full text-[14px] font-medium tabular transition-colors", qty === n ? "bg-foreground text-background" : "bg-secondary hover:bg-secondary/70")} aria-label={`${n} billettar`} data-testid={`door-sale-quick-${n}`}>{n}</button>)}</div>

            {(types || []).length > 1 && (
              <div className="mt-5 flex flex-wrap gap-2" role="radiogroup" aria-label="Billettype" data-testid="door-sale-types">
                {types.map((t) => <button key={t.id} type="button" role="radio" aria-checked={type?.id === t.id} onClick={() => setTypeId(t.id)} className={cn("h-11 rounded-[10px] px-4 text-[14px] font-medium transition-colors", type?.id === t.id ? "bg-foreground text-background" : "bg-secondary hover:bg-secondary/70")} data-testid={`door-sale-type-${t.id}`}>{t.name}{t.price ? ` · ${fmtKr(t.price)}` : ""}</button>)}
              </div>
            )}

            <p className="mt-5 text-center text-[15px] text-muted-foreground tabular" data-testid="door-sale-amount">{amount > 0 ? `${qty} × ${fmtKr(type?.price || 0)} = ` : ""}<span className="font-semibold text-foreground">{amount > 0 ? fmtKr(amount) : "Gratis"}</span></p>
            {overQuota && <p className="mt-2 text-center text-[13px] font-medium text-sol" data-testid="door-sale-over-warning">Berre {left} att i dørkvoten – sal over kvoten må stadfestast.</p>}

            <div className="mt-5 flex gap-3">
              <Big onClick={() => start("cash")} disabled={sell.isPending} data-testid="door-sale-pay-cash">{sell.isPending ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Banknote className="h-5 w-5" /> Kontant</>}</Big>
              <Big onClick={() => start("vipps")} disabled={sell.isPending} className="bg-[#ff5b24] text-white hover:bg-[#e8511f]" data-testid="door-sale-pay-vipps">{sell.isPending ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Smartphone className="h-5 w-5" /> Vipps</>}</Big>
            </div>
            <p className="mt-3 text-center text-[12px] text-muted-foreground">Eitt trykk registrerer salet. Ingen e-post, ingen QR – dei står jo her.</p>
          </div>
        </DrawerContent>
      </Drawer>

      <ConfirmDialog open={!!overAsk} onOpenChange={(o) => !o && setOverAsk(null)} title="Dørkvoten er full – selje likevel?" text={`Det er ${left ?? 0} ${left === 1 ? "plass" : "plassar"} att i dørkvoten, og du vil selje ${overAsk?.body?.qty}. Du har ansvaret for at det er plass i salen.`} confirmLabel="Sel likevel" loading={sell.isPending} onConfirm={() => fire({ ...overAsk.body, allow_over: true }, overAsk.key)} testId="door-sale-over-dialog" />
    </div>
  );
};
